import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Euro, 
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Calendar,
  CheckCircle2,
  Home,
  FileText,
  Receipt,
  Building2,
  AlertCircle,
  Clock,
  TrendingUp,
  Hash,
  Lock,
  Wallet,
  MinusCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, subMonths, addMonths, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== PDF GENERATION ====================
const generateInvoicePDF = async (invoice) => {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  
  const doc = new jsPDF();
  const blue = [31, 58, 138]; // #1F3A8A
  
  // Header - Logo area
  doc.setFillColor(...blue);
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setFontSize(24);
  doc.setTextColor(255, 255, 255);
  doc.text('FACTURE', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`N° ${invoice.invoice_number}`, 105, 30, { align: 'center' });
  
  // Provider info (FDM - Left)
  doc.setFontSize(11);
  doc.setTextColor(...blue);
  doc.text('ÉMETTEUR', 14, 55);
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const provider = invoice.provider || {};
  doc.text(provider.company_name || provider.name || 'N/A', 14, 63);
  if (provider.company_address) {
    doc.text(provider.company_address, 14, 70);
  }
  doc.text(`SIRET: ${provider.siret || 'Non renseigné'}`, 14, 77);
  if (provider.email) doc.text(`Email: ${provider.email}`, 14, 84);
  if (provider.phone) doc.text(`Tél: ${provider.phone}`, 14, 91);
  
  // Client info (Conciergerie - Right)
  doc.setFontSize(11);
  doc.setTextColor(...blue);
  doc.text('DESTINATAIRE', 120, 55);
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const client = invoice.client || {};
  doc.text(client.name || 'MA CONCIERGERIE BRETONNE', 120, 63);
  doc.text(client.address || '40 BIS RUE DE DINARD', 120, 70);
  doc.text(client.city || '35730 PLEURTUIT', 120, 77);
  
  // Invoice details
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(14, 100, 182, 25, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setTextColor(...blue);
  const periodLabel = `${format(new Date(invoice.year, invoice.month - 1), 'MMMM yyyy', { locale: fr })}`;
  doc.text(`Période: ${periodLabel}`, 20, 110);
  doc.text(`Date d'émission: ${format(new Date(invoice.issued_date), 'dd/MM/yyyy')}`, 20, 118);
  doc.text(`Échéance: Sous 15 jours ouvrés`, 120, 110);
  
  // Table
  const tableData = (invoice.lines || []).map(line => [
    line.description,
    line.quantity.toString(),
    `${line.unit_price.toFixed(2)} €`,
    `${line.total.toFixed(2)} €`
  ]);
  
  autoTable(doc, {
    startY: 135,
    head: [['Description', 'Qté', 'Prix unitaire', 'Total']],
    body: tableData,
    theme: 'striped',
    headStyles: { 
      fillColor: blue,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      fontSize: 9,
      cellPadding: 5
    },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 25, halign: 'center' },
      2: { cellWidth: 35, halign: 'right' },
      3: { cellWidth: 35, halign: 'right' }
    }
  });
  
  // Total box (extended if advances were deducted)
  const finalY = doc.lastAutoTable.finalY + 10;
  const hasAdvances = (invoice.advance_deduction_total || 0) > 0;
  const boxHeight = hasAdvances ? 52 : 35;
  
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(110, finalY, 86, boxHeight, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text('Total HT:', 115, finalY + 10);
  doc.text(`${invoice.total_ht.toFixed(2)} €`, 190, finalY + 10, { align: 'right' });
  
  doc.text('TVA:', 115, finalY + 18);
  doc.text('0,00 €', 190, finalY + 18, { align: 'right' });
  
  doc.setFontSize(12);
  doc.setTextColor(...blue);
  doc.setFont(undefined, 'bold');
  doc.text('Total TTC:', 115, finalY + 28);
  doc.text(`${invoice.total_ttc.toFixed(2)} €`, 190, finalY + 28, { align: 'right' });
  doc.setFont(undefined, 'normal');

  let mentionY = finalY + 45;
  
  if (hasAdvances) {
    doc.setFontSize(10);
    doc.setTextColor(180, 83, 9); // amber-700
    doc.text('Avances déduites:', 115, finalY + 38);
    doc.text(`-${(invoice.advance_deduction_total || 0).toFixed(2)} €`, 190, finalY + 38, { align: 'right' });

    doc.setFontSize(12);
    doc.setTextColor(5, 150, 105); // emerald-600
    doc.setFont(undefined, 'bold');
    doc.text('Net à payer:', 115, finalY + 47);
    const netToPay = (invoice.net_to_pay != null)
      ? invoice.net_to_pay
      : (invoice.total_ttc - (invoice.advance_deduction_total || 0));
    doc.text(`${netToPay.toFixed(2)} €`, 190, finalY + 47, { align: 'right' });
    doc.setFont(undefined, 'normal');

    mentionY = finalY + 62;

    // Detail of advance deductions (below the table)
    if (Array.isArray(invoice.advance_deductions) && invoice.advance_deductions.length > 0) {
      doc.setFontSize(9);
      doc.setTextColor(120, 53, 15);
      doc.text('Détail des avances déduites :', 14, finalY + 10);
      let dy = finalY + 16;
      invoice.advance_deductions.slice(0, 6).forEach((d) => {
        const text = `• ${d.reason || 'Avance'} — ${(d.amount || 0).toFixed(2)} €`;
        doc.text(text, 14, dy);
        dy += 5;
      });
    }
  }
  
  // TVA mention
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(invoice.tva_mention || 'TVA non applicable, art. 293 B du CGI', 14, mentionY);
  
  // Footer
  doc.setFillColor(...blue);
  doc.rect(0, 277, 210, 20, 'F');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text('Facture générée via PlayMCB - MA CONCIERGERIE BRETONNE', 105, 287, { align: 'center' });
  
  // Save
  const fileName = `Facture-${invoice.invoice_number}.pdf`;
  doc.save(fileName);
  return fileName;
};

// ==================== MAIN COMPONENT ====================
const HousekeeperEarnings = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [invoiceData, setInvoiceData] = useState(null);
  const [myInvoices, setMyInvoices] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'history'

  const fetchInvoiceData = useCallback(async () => {
    setLoading(true);
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      
      const response = await fetch(`${API_URL}/api/invoices/my-data/${month}/${year}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoiceData(data);
      }
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, token]);

  const fetchMyInvoices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/invoices/my-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchInvoiceData();
    fetchMyInvoices();
  }, [fetchInvoiceData, fetchMyInvoices]);

  // Auto-refetch when the FDM returns to this page or opens the app
  // Ensures the "Mes Revenus" reflects the latest task.price after admin recalculation
  useEffect(() => {
    const refetch = () => {
      fetchInvoiceData();
      fetchMyInvoices();
    };
    const onFocus = () => refetch();
    const onVisibility = () => { if (document.visibilityState === 'visible') refetch(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchInvoiceData, fetchMyInvoices]);

  const previousMonth = () => setSelectedMonth(subMonths(selectedMonth, 1));
  const nextMonth = () => setSelectedMonth(addMonths(selectedMonth, 1));

  // Download Factur-X PDF (PDF/A-3 + embedded CII XML, BASIC profile)
  // Generated by backend, importable in Zervant, Pennylane, Sage, Chorus Pro, etc.
  const downloadFacturxPdf = async (invoiceId, invoiceNumber) => {
    const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/pdf`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Erreur téléchargement Factur-X');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Facture-${invoiceNumber || invoiceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const handleGenerateInvoice = async () => {
    setIsGenerating(true);
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      
      const response = await fetch(`${API_URL}/api/invoices/generate?month=${month}&year=${year}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const invoice = await response.json();
        await downloadFacturxPdf(invoice.id, invoice.invoice_number);
        toast.success('Facture Factur-X générée et téléchargée !');
        fetchMyInvoices();
        fetchInvoiceData();
      } else {
        toast.error('Erreur lors de la génération');
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadExisting = async (invoice) => {
    try {
      await downloadFacturxPdf(invoice.id, invoice.invoice_number);
      toast.success('Facture téléchargée !');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const monthName = format(selectedMonth, 'MMMM yyyy', { locale: fr });
  const canGoNext = selectedMonth < new Date();

  // Vérifier si on peut générer/télécharger la facture (seulement à partir du 1er du mois suivant)
  const getInvoiceAvailableDate = (month) => {
    // La facture du mois X est disponible à partir du 1er du mois X+1
    return startOfDay(addMonths(startOfMonth(month), 1));
  };
  
  const invoiceAvailableDate = getInvoiceAvailableDate(selectedMonth);
  const today = startOfDay(new Date());
  const canAccessInvoice = !isBefore(today, invoiceAvailableDate);
  
  // Formater la date de disponibilité
  const availableDateFormatted = format(invoiceAvailableDate, 'd MMMM yyyy', { locale: fr });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F3A8A] to-[#2D4A9A] text-white p-6 pb-20">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Mes Revenus</h1>
            <p className="text-blue-200 text-sm mt-1">Factures auto-entrepreneur</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Receipt className="h-6 w-6" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 -mt-14 space-y-4">
        {/* Month selector */}
        <Card className="shadow-lg border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={previousMonth} data-testid="earnings-prev-month-btn">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="text-center">
                <p className="text-lg font-semibold text-slate-800 capitalize" data-testid="earnings-month-label">{monthName}</p>
                <p className="text-xs text-slate-500">Sélectionnez un mois</p>
              </div>
              <Button variant="ghost" size="icon" onClick={nextMonth} disabled={!canGoNext} data-testid="earnings-next-month-btn">
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'current' ? 'default' : 'outline'}
            className={activeTab === 'current' ? 'flex-1 bg-[#1F3A8A]' : 'flex-1'}
            onClick={() => setActiveTab('current')}
            data-testid="earnings-tab-current"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Ce mois
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'outline'}
            className={activeTab === 'history' ? 'flex-1 bg-[#1F3A8A]' : 'flex-1'}
            onClick={() => setActiveTab('history')}
            data-testid="earnings-tab-history"
          >
            <FileText className="h-4 w-4 mr-2" />
            Historique
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#1F3A8A]" />
          </div>
        ) : activeTab === 'current' ? (
          <>
            {/* Summary Card */}
            <Card className="shadow-lg border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Total du mois</p>
                    <p className="text-3xl font-bold">{invoiceData?.total_amount?.toFixed(2) || '0.00'} €</p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-100 text-sm">Prestations</p>
                    <p className="text-2xl font-bold">{invoiceData?.total_tasks || 0}</p>
                  </div>
                </div>
              </div>
              
              {/* Invoice status */}
              <CardContent className="p-4">
                {!canAccessInvoice ? (
                  /* Facture pas encore disponible */
                  <div className="bg-slate-100 p-4 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <Lock className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">Facture non disponible</p>
                        <p className="text-xs text-slate-500">Le mois n'est pas encore terminé</p>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600 text-center">
                        <Calendar className="h-4 w-4 inline mr-1 text-[#1F3A8A]" />
                        Disponible à partir du <strong className="text-[#1F3A8A]">{availableDateFormatted}</strong>
                      </p>
                    </div>
                  </div>
                ) : invoiceData?.existing_invoice ? (
                  <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-800">Facture générée</p>
                        <p className="text-xs text-blue-600">{invoiceData.existing_invoice.invoice_number}</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDownloadExisting(invoiceData.existing_invoice)}
                      data-testid="earnings-download-existing-pdf-btn"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                ) : invoiceData?.total_tasks > 0 ? (
                  <Button 
                    className="w-full h-12 bg-[#1F3A8A] hover:bg-[#162d6d]"
                    onClick={handleGenerateInvoice}
                    disabled={isGenerating}
                    data-testid="earnings-generate-invoice-btn"
                  >
                    {isGenerating ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <FileText className="h-5 w-5 mr-2" />
                    )}
                    Générer ma facture
                  </Button>
                ) : (
                  <div className="flex items-center gap-3 text-slate-500 bg-slate-50 p-3 rounded-lg">
                    <AlertCircle className="h-5 w-5" />
                    <p className="text-sm">Aucune prestation ce mois-ci</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Advances section — visible whenever there are outstanding advances OR the existing invoice deducted some */}
            {((invoiceData?.advances_outstanding_total || 0) > 0 ||
              (invoiceData?.existing_invoice?.advance_deduction_total || 0) > 0) && (
              <Card className="shadow-lg border-0 overflow-hidden" data-testid="earnings-advances-card">
                <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-5 w-5" />
                    <p className="font-semibold">Avances sur paiement</p>
                  </div>
                </div>
                <CardContent className="p-4 space-y-3">
                  {invoiceData?.existing_invoice?.advance_deduction_total > 0 ? (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Total facturé</span>
                        <span className="font-medium text-slate-800">
                          {invoiceData.existing_invoice.total_ttc?.toFixed(2)} €
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-amber-700 flex items-center gap-1">
                          <MinusCircle className="h-4 w-4" />
                          Avances déduites
                        </span>
                        <span className="font-medium text-amber-700">
                          -{invoiceData.existing_invoice.advance_deduction_total.toFixed(2)} €
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t pt-2">
                        <span className="text-emerald-700 font-semibold">Net à payer</span>
                        <span className="font-bold text-emerald-700 text-lg">
                          {(invoiceData.existing_invoice.net_to_pay ?? (invoiceData.existing_invoice.total_ttc - invoiceData.existing_invoice.advance_deduction_total)).toFixed(2)} €
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Solde d'avances</span>
                        <span className="font-medium text-amber-700" data-testid="earnings-outstanding-total">
                          {(invoiceData.advances_outstanding_total || 0).toFixed(2)} €
                        </span>
                      </div>
                      {invoiceData?.advance_deduction_preview > 0 && (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-amber-700 flex items-center gap-1">
                              <MinusCircle className="h-4 w-4" />
                              À déduire de cette facture
                            </span>
                            <span className="font-medium text-amber-700">
                              -{invoiceData.advance_deduction_preview.toFixed(2)} €
                            </span>
                          </div>
                          <div className="flex items-center justify-between border-t pt-2">
                            <span className="text-emerald-700 font-semibold">Net estimé</span>
                            <span className="font-bold text-emerald-700 text-lg" data-testid="earnings-net-preview">
                              {(invoiceData.net_amount_preview || 0).toFixed(2)} €
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {invoiceData?.advances_outstanding?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-slate-500 cursor-pointer">
                        Détail ({invoiceData.advances_outstanding.length} avance{invoiceData.advances_outstanding.length > 1 ? 's' : ''})
                      </summary>
                      <div className="mt-2 space-y-1">
                        {invoiceData.advances_outstanding.map((a) => (
                          <div key={a.id} className="text-xs text-slate-600 flex justify-between py-1 border-b border-slate-100">
                            <span className="truncate mr-2">{a.reason}</span>
                            <span className="font-medium whitespace-nowrap">{a.remaining_amount.toFixed(2)} €</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Prestations by property */}
            {invoiceData?.by_property?.length > 0 && (
              <Card className="shadow-lg border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Home className="h-4 w-4 text-[#1F3A8A]" />
                    Détail par logement
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {invoiceData.by_property.map((item, idx) => (
                      <div key={item.property_id || `${item.property_name}-${idx}`} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-[#1F3A8A]" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">{item.property_name}</p>
                            <p className="text-xs text-slate-500">{item.count} prestation(s)</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-800">{item.total.toFixed(2)} €</p>
                          <p className="text-xs text-slate-500">{item.unit_price.toFixed(2)} €/u</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Info Card - Facturation */}
            <Card className="shadow-sm border border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Facturation auto-entrepreneur</p>
                    <p className="text-blue-600">
                      Vos factures sont conformes à la réglementation française. 
                      Mention &quot;TVA non applicable, art. 293 B du CGI&quot; incluse.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Card - Envoi et Paiement */}
            <Card className="shadow-sm border border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="text-sm text-amber-900 space-y-3">
                  <div className="flex gap-3">
                    <Euro className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold mb-1">Envoi des factures</p>
                      <p className="text-amber-700">
                        Envoyez vos factures à : <strong>facturation@maconciergerie-bretonne.fr</strong>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Clock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold mb-1">Délai de paiement</p>
                      <p className="text-amber-700">
                        Paiement sous <strong>15 jours ouvrés</strong> après contrôle et éventuel remboursement des voyageurs.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-700 mb-1">Important</p>
                      <p className="text-red-600">
                        Toute facture envoyée <strong>avant le 2 du mois</strong> sera automatiquement rejetée.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* History tab */
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#1F3A8A]" />
                Mes factures
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {myInvoices.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune facture générée</p>
                  <p className="text-sm mt-1">Générez votre première facture depuis l&apos;onglet &quot;Ce mois&quot;</p>
                </div>
              ) : (
                <div className="divide-y">
                  {myInvoices.map((invoice) => (
                    <div key={invoice.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{invoice.invoice_number}</p>
                          <p className="text-xs text-slate-500">
                            {format(new Date(invoice.year, invoice.month - 1), 'MMMM yyyy', { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold text-slate-800">{invoice.total_ttc.toFixed(2)} €</p>
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-xs">
                            Générée
                          </Badge>
                        </div>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => handleDownloadExisting(invoice)}
                          data-testid={`earnings-download-invoice-${invoice.invoice_number}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Profile reminder */}
        {!user?.siret && (
          <Card className="shadow-sm border border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 mb-1">Complétez votre profil</p>
                  <p className="text-amber-700">
                    Ajoutez votre SIRET et adresse professionnelle pour des factures complètes.
                  </p>
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-amber-800 font-medium mt-1"
                    onClick={() => navigate('/profile')}
                  >
                    Modifier mon profil →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default HousekeeperEarnings;
